import { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIgreja } from "@/core/hooks/useIgreja";
import { useVoltarOu } from "@/modules/visitantes/hooks/useVoltarOu";
import { FluxoCadastro } from "@/modules/visitantes/components/FluxoCadastro";
import { SemPermissao } from "@/modules/visitantes/components/PaginaFormulario";
import {
  useInformarVisitante,
  useMinisteriosQueLidero,
  usePodeInformar,
  useVisitantes,
} from "@/modules/visitantes/hooks/useVisitantes";
import type {
  DadosFamiliar,
  DadosVisitante,
} from "@/modules/visitantes/services/visitantesService";

/**
 * Líder/gestor de outro ministério cadastra um Visitante pendente com o mesmo formulário completo
 * (família, aviso e consentimento); o ministério responsável decide se adiciona (VIS-DEC-038).
 */
export function InformarVisitantePage() {
  const navigate = useNavigate();
  const voltar = useVoltarOu("/visitantes");
  const protecao = useProtecaoFormulario();
  const { podeInformar, carregando } = usePodeInformar();
  const { data: ministerios = [] } = useMinisteriosQueLidero(podeInformar);
  const { data: existentes = [] } = useVisitantes(podeInformar);
  const { data: igreja } = useIgreja();
  const informar = useInformarVisitante();
  const [erro, setErro] = useState<string | null>(null);

  if (carregando) return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;
  if (!podeInformar)
    return (
      <SemPermissao texto="Só líderes e gestores de outros ministérios cadastram Visitantes pendentes. O ministério responsável cadastra direto." />
    );

  const contato = igreja
    ? { email: igreja.contato_email, telefone: igreja.contato_telefone }
    : null;

  async function enviar(
    dados: DadosVisitante,
    consentimento: boolean,
    familiares: DadosFamiliar[],
  ) {
    setErro(null);
    try {
      await informar.mutateAsync({ dados, familiares, consentimento });
      protecao.liberar();
      const quem =
        familiares.length > 0
          ? `${dados.nome} e mais ${familiares.length} da família foram cadastrados`
          : `${dados.nome} foi cadastrado(a)`;
      navigate("/visitantes?aba=meus", {
        replace: true,
        state: {
          aviso: `${quem} como visitante pendente. O ministério responsável vai avaliar.`,
        },
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cadastrar o visitante pendente.");
    }
  }

  return (
    <FluxoCadastro
      modo="informe"
      protecao={protecao}
      titulo="Novo visitante"
      descricao="Conheceu alguém que visitou a igreja? Cadastre a pessoa e o ministério responsável decide se adiciona. Enquanto isso ela fica como Visitante pendente."
      ministerios={ministerios}
      existentes={existentes}
      contatoIgreja={contato}
      enviando={informar.isPending}
      erro={erro}
      onLimparErro={() => setErro(null)}
      onEnviarSimples={(dados, consentimento) => enviar(dados, consentimento, [])}
      onEnviarFamilia={(principal, membros, consentimento) =>
        enviar(principal, consentimento, membros)
      }
      rotuloEnviarFamilia={() => "Cadastrar"}
      onCancelar={voltar}
    />
  );
}
